import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const defaultRole = params.get("role") === "babysitter" ? "babysitter" : "parent";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: defaultRole });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (res.ok) { toast.success("Welcome to Sakeenah Circle"); nav("/dashboard"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Join Sakeenah Circle" path="/register" description="Create a free Sakeenah Circle account — as a parent or caregiver." noindex />
      <Nav />
      <div className="max-w-md mx-auto px-5 py-16">
        <h1 className="font-serif text-4xl text-forest">Join the circle</h1>
        <p className="text-forest/70 mt-2">Warm, verified care starts here.</p>
        <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-taupe/30 rounded-full" data-testid="role-toggle">
          {["parent", "babysitter"].map((r) => (
            <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
              data-testid={`role-${r}`}
              className={`rounded-full py-2 text-sm capitalize transition-colors ${form.role === r ? "bg-forest text-linen" : "text-forest/70"}`}>
              I'm a {r}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Full name</label>
            <input required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-organic mt-1" data-testid="register-name" />
          </div>
          <div>
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Email</label>
            <input type="email" required autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck="false" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-organic mt-1" data-testid="register-email" />
          </div>
          <div>
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Password</label>
            <input type="password" required minLength={6} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-organic mt-1" data-testid="register-password" />
            <p className="text-xs text-forest/60 mt-1">At least 6 characters.</p>
          </div>
          <button disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="register-submit">
            {busy ? "Creating account…" : "Create account"}
          </button>
          <p className="text-sm text-forest/70 text-center">
            Already with us? <Link to="/login" className="text-forest underline underline-offset-4">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
